// frontend/src/pages/Forside.jsx
//
// Forsiden for besøgende, der ikke er logget ind (logget ind -> Mit
// dashboard). Kapitler i hel bredde som Dit strømår, i sidens eget tema:
//   1. "Saml dit forbrug i ét dashboard" (tekster fra de tidligere forsidekort)
//      med eksempel-dashboardet lige under
//      (MitDashboard demo: rigtige tal for elpris, CO2 og Europa;
//      eksempeldata for bilen)
//   2. CO2-beregneren: din tur, hvis du lader nu (RuteNu), kort i hel bredde
//   3. De vigtigste vidensartikler (interne links)
//   4. Til virksomheder: flådelederens dashboard med eksempeldata (MitDashboard demoFlaade)
//   5. En sidste opfordring
// /flaadestyring er samme side som landingsside for virksomheder: egen titel,
// beskrivelse og H1 (flådekapitlet), og siden ruller ned til flådekapitlet.
// Dansk, engelsk og tysk. Ingen ikoner (CLAUDE.md).

import { lazy, Suspense, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageSeo from "../components/PageSeo.jsx";
import "./Forside.css";

const MitDashboard = lazy(() => import("./MitDashboard.jsx"));
const RuteNu = lazy(() => import("../components/RuteNu.jsx"));

// De artikler, folk oftest søger svar på (dansk slug, engelsk slug, titler).
const ARTIKLER = [
  ["hvornaar-er-stroemmen-groennest", "when-is-power-greenest", "Hvornår er strømmen grønnest?", "When is power greenest?"],
  ["hvornaar-skal-jeg-lade-min-elbil", "when-should-i-charge-my-ev", "Hvornår skal jeg lade min elbil?", "When should I charge my EV?"],
  ["hvor-meget-co2-udleder-en-elbil-pr-km", "how-much-co2-does-an-ev-emit-per-km", "Hvor meget CO₂ udleder en elbil pr. km?", "How much CO₂ does an EV emit per km?"],
  ["hvad-er-spotprisen-paa-stroem", "what-is-the-spot-price-of-electricity", "Hvad er spotprisen på strøm?", "What is the spot price of electricity?"],
  ["hvorfor-skifter-elprisen-hvert-kvarter", "why-does-the-electricity-price-change-every-quarter-hour", "Hvorfor skifter elprisen hvert kvarter?", "Why does the electricity price change every quarter hour?"],
  ["elbil-benzin-eller-diesel-paa-ferieturen", "ev-petrol-or-diesel-on-the-holiday-trip", "Elbil, benzin eller diesel på ferieturen?", "EV, petrol or diesel on the holiday trip?"],
];

/**
 * Ruller til elementet og bliver der, mens indholdet over det indlæses
 * (eksempel-dashboardet og beregneren skubber siden ned, når de er klar).
 * Stopper, så snart brugeren selv ruller, eller efter 6 sekunder.
 */
function useRulTil(ref, aktiv) {
  useEffect(() => {
    if (!aktiv || !ref.current) return undefined;
    const el = ref.current;
    let stop = false;
    const rul = () => { if (!stop) el.scrollIntoView({ block: "start" }); };
    const afbryd = () => { stop = true; };
    rul();
    const obs = typeof ResizeObserver !== "undefined" ? new ResizeObserver(rul) : null;
    obs?.observe(document.body);
    const haendelser = ["wheel", "touchstart", "keydown", "mousedown"];
    haendelser.forEach((h) => window.addEventListener(h, afbryd, { passive: true, once: true }));
    const tid = setTimeout(afbryd, 6000);
    return () => {
      stop = true;
      obs?.disconnect();
      clearTimeout(tid);
      haendelser.forEach((h) => window.removeEventListener(h, afbryd));
    };
  }, [ref, aktiv]);
}

export default function Forside({ language = "dk", session = null, fokus = null }) {
  const sprog = language === "dk" ? "da" : language;
  const t = (da, en, de) => (language === "de" ? de : language === "en" ? en : da);
  const navigate = useNavigate();
  const flaade = fokus === "flaade";
  const flaadeRef = useRef(null);
  useRulTil(flaadeRef, flaade);
  // Én H1 pr. side: på /flaadestyring er flådekapitlets overskrift H1.
  const HeroH = flaade ? "h2" : "h1";
  const FlaadeH = flaade ? "h1" : "h2";

  // Logget ind: Mit dashboard er forsiden (men landingssiden for flåder vises stadig).
  useEffect(() => { if (session && !flaade) navigate("/mit-dashboard", { replace: true }); }, [session, flaade, navigate]);

  const henter = (tekst) => <p className="fs-henter">{tekst}</p>;

  return (
    <div className="fs-side">
      {flaade ? (
        <PageSeo
          title={t("Flådestyring: el, brændstof, CO₂ og kørselsbøger for firmabiler | OFFLIST", "Fleet management: electricity, fuel, CO₂ and driving logs for company cars | OFFLIST", "Flottenmanagement: Strom, Kraftstoff, CO₂ und Fahrtenbücher für Firmenwagen | OFFLIST")}
          description={t("Saml hele flådens el, brændstof og CO₂ ét sted. Medarbejderne tilkobler deres biler, og du får kørselsbøger, privatkørsel og rapporter i scope 1, 2 og 3 måned for måned.",
            "Bring the whole fleet's electricity, fuel and CO₂ together. Employees connect their cars, and you get driving logs, private mileage and scope 1, 2 and 3 reports month by month.",
            "Strom, Kraftstoff und CO₂ der ganzen Flotte an einem Ort. Die Mitarbeitenden verbinden ihre Autos, und du bekommst Fahrtenbücher, Privatfahrten und Berichte in Scope 1, 2 und 3 Monat für Monat.")}
          path="/flaadestyring"
          lang={sprog}
        />
      ) : (
        <PageSeo
          title={t("OFFLIST | Forstå betydningen af dit energiforbrug", "OFFLIST | Understand the impact of your energy consumption", "OFFLIST | Verstehe die Wirkung deines Energieverbrauchs")}
          description={t("Saml elbil og husstand ét sted, og se sammenhængen mellem dit energiforbrug, din økonomi og dit klimaaftryk, år efter år. Gratis at prøve.",
            "Bring your EV and home together and see how your energy use, money and climate footprint connect, year after year. Free to try.",
            "Bring E-Auto und Haushalt zusammen und sieh, wie Energieverbrauch, Geld und Klima-Fußabdruck zusammenhängen, Jahr für Jahr. Kostenlos testen.")}
          path="/"
          lang={sprog}
        />
      )}

      <section className="fs-hero" aria-labelledby="fs-h1">
        <div className="fs-indre">
          <HeroH id="fs-h1" className="fs-h1">{t("Forstå betydningen af dit energiforbrug", "Understand the impact of your energy consumption", "Verstehe die Wirkung deines Energieverbrauchs")}</HeroH>
          <p className="fs-undertekst">{t("Ikke endnu en elpris-app eller ladeapp. OFFLIST samler dit energiforbrug og dit klimaaftryk, år efter år.",
            "Not another power price app or charging app. OFFLIST brings together your energy use and your climate footprint, year after year.",
            "Keine weitere Strompreis- oder Lade-App. OFFLIST bringt deinen Energieverbrauch und deinen Klima-Fußabdruck zusammen, Jahr für Jahr.")}</p>
          <div className="fs-knapper">
            <Link className="fs-knap fs-knap-fyldt" to="/signup">{t("Opret gratis konto", "Create free account", "Kostenloses Konto erstellen")}</Link>
            <span className="fs-login">{t("Har du en konto?", "Have an account?", "Schon ein Konto?")} <Link to="/login?next=/mit-dashboard">{t("Log ind", "Log in", "Anmelden")}</Link></span>
          </div>
        </div>
        <div className="fs-eksempel" id="eksempel">
          <Suspense fallback={henter(t("Henter eksemplet ...", "Loading the example ...", "Beispiel wird geladen ..."))}>
            <MitDashboard demo language={language} />
          </Suspense>
        </div>
        <p className="fs-eksempel-note">{t("Elpris, CO₂ og Europa er de rigtige tal. Bilens tal er et eksempel; med din egen elbil tilkoblet via Smartcar er det dine ladninger.",
          "Power price, CO₂ and Europe are the real figures. The car's figures are an example; with your own EV connected via Smartcar they are your charges.",
          "Strompreis, CO₂ und Europa sind die echten Zahlen. Die Zahlen des Autos sind ein Beispiel; mit deinem E-Auto über Smartcar sind es deine Ladungen.")}</p>
      </section>

      <section className="fs-beregner" aria-labelledby="fs-beregner-titel">
        <div className="fs-indre">
          <p className="fs-etiket">{t("CO₂-beregneren", "The CO₂ calculator", "Der CO₂-Rechner")}</p>
          <h2 id="fs-beregner-titel" className="fs-h2">{t("Hvad udleder din tur?", "What does your trip emit?", "Was stößt deine Fahrt aus?")}</h2>
          <p className="fs-tekst">{t("Skriv din typiske tur. Vi tager udgangspunkt i, at du lader op nu, og finder ruten, landene undervejs og strømmens CO₂ i hvert land lige nu, ikke et gennemsnit.",
            "Enter your usual trip. We assume you charge now, and find the route, the countries on the way and each country's power CO₂ right now, not an average.",
            "Gib deine typische Fahrt ein. Wir gehen davon aus, dass du jetzt lädst, und finden die Route, die Länder unterwegs und das CO₂ des Stroms in jedem Land gerade jetzt, keinen Durchschnitt.")}</p>
        </div>
        <Suspense fallback={henter(t("Henter beregneren ...", "Loading the calculator ...", "Rechner wird geladen ..."))}>
          <RuteNu language={language} />
        </Suspense>
      </section>

      {/* De vigtigste vidensartikler (interne links, så Google finder dem). */}
      <section className="fs-kapitel fs-viden" aria-labelledby="fs-viden-titel">
        <div className="fs-indre">
          <h2 id="fs-viden-titel" className="fs-h3">{t("Bliv klogere på strøm og CO₂", "Learn more about power and CO₂", "Mehr über Strom und CO₂")}</h2>
          <ul className="fs-artikler">
            {ARTIKLER.map(([da, en, daTitel, enTitel]) => (
              <li key={da}><Link to={language === "en" ? `/en/knowledge/${en}` : `/viden/${da}`}>{language === "en" ? enTitel : daTitel} →</Link></li>
            ))}
          </ul>
          <p className="fs-tekst-lille">
            <Link className="fs-link" to={language === "en" ? "/en/knowledge" : "/viden"}>{t("Alle artikler om elpris, grøn strøm og elbiler", "All articles on power prices, green power and EVs", "Alle Artikel über Strompreis, grünen Strom und E-Autos")} →</Link>
            {" · "}
            <Link className="fs-link" to="/energi">{t("Dagens elpris og CO₂", "Today's power price and CO₂", "Strompreis und CO₂ heute")} →</Link>
          </p>
        </div>
      </section>

      {/* Til virksomheder: flådelederens dashboard med eksempeldata. Bygget som
          en landingsside ligesom toppen, og H1 på /flaadestyring. */}
      <section className="fs-kapitel fs-erhverv fs-landing" id="flaade" ref={flaadeRef} aria-labelledby="fs-erhverv-titel">
        <div className="fs-indre">
          <p className="fs-etiket">{t("Flådestyring til virksomheder", "Fleet management for businesses", "Flottenmanagement für Unternehmen")}</p>
          <FlaadeH id="fs-erhverv-titel" className="fs-h1">{t("Hele flådens forbrug og CO₂ ét sted", "The whole fleet's usage and CO₂ in one place", "Verbrauch und CO₂ der ganzen Flotte an einem Ort")}</FlaadeH>
          <p className="fs-undertekst">{t("Medarbejderne tilkobler deres biler, og du får flådens el, brændstof og CO₂ måned for måned, kørselsbøger og rapporter i scope 1, 2 og 3 til klimaregnskabet.",
            "Employees connect their cars, and you get the fleet's electricity, fuel and CO₂ month by month, driving logs and scope 1, 2 and 3 reports for your climate accounts.",
            "Die Mitarbeitenden verbinden ihre Autos, und du bekommst Strom, Kraftstoff und CO₂ der Flotte Monat für Monat, Fahrtenbücher und Berichte in Scope 1, 2 und 3 für die Klimabilanz.")}</p>
          <ul className="fs-punkter">
            <li>{t("Elbiler via Smartcar: hver ladning med kWh, pris og CO₂ time for time", "EVs via Smartcar: every charge with kWh, price and CO₂ hour by hour", "E-Autos über Smartcar: jede Ladung mit kWh, Preis und CO₂ Stunde für Stunde")}</li>
            <li>{t("Benzin og diesel fra tankkortet (CSV)", "Petrol and diesel from the fuel card (CSV)", "Benzin und Diesel von der Tankkarte (CSV)")}</li>
            <li>{t("Kørselsbog og privatkørsel i firmabiler", "Driving log and private mileage in company cars", "Fahrtenbuch und Privatfahrten in Firmenwagen")}</li>
            <li>{t("Måneds- og årsrapporter pr. nummerplade til klimaregnskabet", "Monthly and annual reports per licence plate for your climate accounts", "Monats- und Jahresberichte pro Kennzeichen für die Klimabilanz")}</li>
          </ul>
          <div className="fs-knapper">
            <Link className="fs-knap fs-knap-fyldt" to="/billing">{t("Se priser", "See pricing", "Preise ansehen")}</Link>
            <Link className="fs-knap" to="/erhverv">{t("Læs om flådeløsningen", "About the fleet solution", "Zur Flottenlösung")}</Link>
          </div>
        </div>
        <div className="fs-eksempel">
          <Suspense fallback={henter(t("Henter eksemplet ...", "Loading the example ...", "Beispiel wird geladen ..."))}>
            <MitDashboard demo demoFlaade language={language} />
          </Suspense>
        </div>
        <p className="fs-eksempel-note">{t("Flådens tal er et eksempel på en tænkt virksomhed. ", "The fleet's figures are an example for an imaginary company. ", "Die Zahlen der Flotte sind ein Beispiel für ein erfundenes Unternehmen. ")}<Link to="/erhverv">{t("Læs om flådeløsningen", "About the fleet solution", "Zur Flottenlösung")} →</Link></p>
      </section>

      <section className="fs-kapitel fs-slut">
        <div className="fs-indre">
          <h2 className="fs-h2">{t("Klar til at bygge dit eget?", "Ready to build your own?", "Bereit, dein eigenes zu bauen?")}</h2>
          <div className="fs-knapper">
            <Link className="fs-knap fs-knap-fyldt" to="/signup">{t("Opret gratis konto", "Create free account", "Kostenloses Konto erstellen")}</Link>
            <Link className="fs-knap" to="/viden/byg-dit-eget-dashboard">{t("Sådan bygger du det", "How to build it", "So baust du es")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
